import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { Product, ProductOrderHistory } from '../../../core/models/product'
import { SKU } from '../../models/sku'
import { ApiImage } from '../../../core/models/product/image'
import { AddCartEvent, CartProduct } from '../../../cart/models/cart-models';
import { Stock } from '../../models/stock';
import chaining from '../../../core/helpers/chaining';
import { Image } from 'angular-modal-gallery';
import { Price } from '../../../core/models/product/price';
import { minQuantity, stepping, unitPrice, upwardsQuantity } from '../../../core/helpers/prices';
import { canBuyProduct } from '../../../core/helpers/product';

import { QuantityChangedEvent } from '../../../shared/models/quantity-changed-event';
import { FavoriteProduct, ToggleFavoriteEvent } from '../../models/favorite-product';
import { Setting } from '../../../core/models/setting';
import { TileFacet } from '../../models/tile-facet';
import findTileFacets from '../../../core/helpers/tile-facet';
import { CombinedFacets } from '../../../facets/models/facet';

import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-details-page',
  templateUrl: './details-page.component.html',
  styleUrls: ['./details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DetailsPageComponent implements OnChanges {
  @Input() loading: boolean
  @Input() product: Product
  @Input() selectedProductOrderHistory: ProductOrderHistory[]
  @Input() selectedProductBackorders: ProductOrderHistory[]
  @Input() combinedFacets: CombinedFacets
  @Input() favoritesProducts: FavoriteProduct[]
  @Input() stock: { [code: string]: Stock }
  @Input() skus: SKU[]
  @Input() cartProducts: { [product: string]: CartProduct }
  @Input() detailsPageSettings: Setting
  @Input() currentDate: string
  @Input() quantityPicker: string
  @Input() showSalesOrderPriceInfo: boolean
  @Input() allowOutOfStockOrdering: boolean
  @Input() canDownloadImage: boolean = false

  @Output() onAddCart = new EventEmitter<AddCartEvent>()
  @Output() onFavoriteToggle = new EventEmitter<ToggleFavoriteEvent>()
  @Output() onClose = new EventEmitter()

  productImage: Image
  imgUrl: string
  startQuantity = 0
  quantity: number
  isFavorite: boolean
  showGallery: boolean = false
  galleryMode: boolean = false
  showMore: boolean = false

  ngOnChanges(changes: SimpleChanges) {
    this.getProductImage()

    this.isFavorite = this.getIsFavorite(this.product)

    this.getActualQuantity(this.cartProduct && this.cartProduct.quantity)
  }

  getProductImage() {
    if (this.product && this.product.assets && this.product.assets.imgs)
      this.productImage = new Image(
        +new Date(),
        {
          img: (this.product.assets.imgs
            .find(img => img.type === 'zoom') || {} as ApiImage )
            .src
        }
      )
  }

  get hasPrice(): Boolean {
    return !!(this.product && this.product.price)
  }

  get productFacets(): TileFacet[] {
    return findTileFacets(chaining(this.detailsPageSettings, 'showFacets'),
      chaining(this.product, 'facets'), this.combinedFacets)
  }


  get cartProduct(): CartProduct {
    if(this.product && this.product._id)
      return this.cartProducts[this.product._id]
  }

  get filteredStockInfo(): Stock[] {
    if (chaining(this.product, 'stock')) {
      return this.product.stock.sort(((a, b) => {
        if (a.date < b.date)
          return -1
        if (a.date > b.date)
          return 1
        return 0
      }))
    } else
      return []
  }
	get currentStockInfo(): Stock {
		if (this.filteredStockInfo && this.filteredStockInfo.length > 0) {
			return this.filteredStockInfo.find(stockItem => stockItem.type === 'current')
		}
		return
	}

	get futureStockInfos(): Stock[] {
		if (this.filteredStockInfo && this.filteredStockInfo.length > 0) {
			return this.filteredStockInfo.filter(stockItem => stockItem.type === 'future')
		}

		return []
	}

	get totalInPurchaseOrderStock(): Stock {
		if (this.filteredStockInfo && this.filteredStockInfo.length > 0) {
			return this.filteredStockInfo.find(stockItem => stockItem.type === 'totalInPurchaseOrders')
		}
	}

	get totalFreeStock(): Stock {
		if (this.filteredStockInfo && this.filteredStockInfo.length > 0) {
			return this.filteredStockInfo.find(stockItem => stockItem.type === 'freeStock')
		}
  }

  get hasMoreInfoUrl(): boolean {
    return !!this.product.moreInfoUrl
  }

  getActualQuantity(cartQuantity) {
    this.startQuantity = cartQuantity > 0
      ? cartQuantity
      : 0
  }

  get name(): string {
    return chaining(this.product, 'name');
  }

  get code(): string {
    return chaining(this.product, 'code');
  }

  get displayCode(): string {
    return chaining(this.product, 'displayCode');
  }

  get prices(): Price[] {
    return chaining(this.product, 'price.prices')
  }

  get stepping(): number {
    return stepping(this.product)
  }

  get actualStepping(): number {
    return chaining(this.product, 'price.stepping')
  }

  get minQuantity(): number {
    return minQuantity(this.product)
  }

  get actualQuantity(): number {
    return upwardsQuantity(this.product, this.quantity || this.startQuantity)
  }

  get hasMultiplePrices(): boolean {
    return chaining(this.prices, 'length') > 1;
  }

  get currentPrice(): Price {
    return unitPrice(this.product, this.actualQuantity || this.minQuantity)
  }

  get canBuy(): boolean {
    return canBuyProduct(this.product, this.allowOutOfStockOrdering);
  }

  get showQuantityPickerAnimated(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase() === 'animated')
  }

  get quantityPickerRelative(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase()) === 'relative'
  }

  get showAttributes(): boolean {
    return this.detailsPageSettings.showAttributes && this.detailsPageSettings.showAttributes.length > 0
  }

  get showFacets(): boolean {
    return this.detailsPageSettings.showFacets && this.detailsPageSettings.showFacets.length > 0
  }

  get showAdditionalProductDetails(): boolean {
    return this.showAttributes || this.showFacets
  }

  quantityChanged(event: QuantityChangedEvent) {
    this.startQuantity = chaining(event, 'quantity');
    this.onAddCart.emit({
      product: chaining(this, 'product._id'),
      quantity: chaining(event, 'quantity')
    });
  }

  toggleFavorite(e: MouseEvent) {
    e.stopPropagation()
    this.onFavoriteToggle.emit({
      isFavorite: this.isFavorite,
      product: this.product
    })
  }

  getIsFavorite(product): boolean {
    return !!this.favoritesProducts.find(item => product && product.code === item.productCode)
  }

  toggleShowMore(): void {
    this.showMore = !this.showMore
  }

  addCart(event: AddCartEvent) {
    this.onAddCart.emit(event)
  }

  close() {
    this.onClose.emit()
  }

  toggleGallery() {
    this.showGallery = !this.showGallery;
  }

  toggleGalleryMode() {
    this.galleryMode = !this.galleryMode;
  }

  onImageError(url: string) {
    this.imgUrl = url
  }

  downloadImage(url: string) {
    const fileName = url.substr(url.lastIndexOf('/') + 1) + '.jpg';
    FileSaver.saveAs(url, fileName);
  }
}
